import { RequestBuilder, fetchAndRetryIfServerBusy } from '@cvent/nucleus-networking';
import { ServiceError } from '../utils/errors';

/**
 * The lookups service client.
 */
export default class LookupClient {
  constructor(baseURL, eventId, environment) {
    this.lookupBaseUrl = baseURL + 'lookups/v1';
    this.environment = environment;
    this.request = new RequestBuilder({ url: this.lookupBaseUrl })
      .withCookies()
      .header('Cvent-Event-ID', eventId);
  }

  /**
   * Retrieves the countries from the lookups.
   */
  async getCountries(authToken, locale) {
    const countriesUrl = this.lookupBaseUrl + '/countries';
    const request = this.request
      .url(countriesUrl)
      .query('locale', locale)
      .query('environment', this.environment)
      .get()
      .auth(authToken)
      .build();
    const response = await fetchAndRetryIfServerBusy(request);
    if (!response.ok) {
      throw await ServiceError.create('getCountries failed', response, request);
    }
    return await response.json();
  }

  /**
   * Retrieves the states based on the selected country from the lookups.
   */
  async getStates(authToken, countryCode, locale) {
    const statesUrl = this.lookupBaseUrl + '/states';
    const request = this.request
      .url(statesUrl)
      .query('countryCode', countryCode)
      .query('locale', locale)
      .query('environment', this.environment)
      .get()
      .auth(authToken)
      .build();
    const response = await fetchAndRetryIfServerBusy(request);
    if (!response.ok) {
      throw await ServiceError.create('getStates failed', response, request);
    }
    return await response.json();
  }

  /**
   * Retrieves the timzone based on the timezone id.
   */
  async getTimezone(authToken, timezoneId, locales) {
    const request = this.request
      .url(`${this.lookupBaseUrl}/timezones`)
      .query('id', timezoneId)
      .query('locale', locales)
      .query('environment', this.environment)
      .get()
      .auth(authToken)
      .build();
    const response = await fetchAndRetryIfServerBusy(request);
    if (!response.ok) {
      throw await ServiceError.create('getTimezones failed', response, request);
    }
    return await response.json();
  }

  /**
   * retrieves airports by given ids
   */
  async getAirports(authToken, airportIds) {
    const request = this.request
      .url(`${this.lookupBaseUrl}/airports`)
      .query('environment', this.environment)
      .post()
      .auth(authToken)
      .json(airportIds)
      .build();
    const response = await fetchAndRetryIfServerBusy(request);
    if (!response.ok) {
      throw await ServiceError.create('getAirports failed', response, request);
    }
    return await response.json();
  }

  /**
   * retrieves airports by search text
   */
  async searchAirports(authToken, searchText) {
    const request = this.request
      .url(`${this.lookupBaseUrl}/airports/search`)
      .query('searchTerm', searchText)
      .query('environment', this.environment)
      .get()
      .auth(authToken)
      .build();
    const response = await fetchAndRetryIfServerBusy(request);
    if (!response.ok) {
      throw await ServiceError.create('searchAirports failed', response, request);
    }
    return await response.json();
  }

  /**
   * Retrieves full list of currencies.
   */
  async getCurrencies(authToken, locale) {
    const request = this.request
      .url(`${this.lookupBaseUrl}/currencies`)
      .query('locale', locale)
      .get()
      .auth(authToken)
      .build();
    const response = await fetchAndRetryIfServerBusy(request);
    if (!response.ok) {
      throw await ServiceError.create('getCurrencies failed', response, request);
    }
    return await response.json();
  }
}
